<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import { useModal } from '@/composition/use-modal';
  import { usePosMoney } from '@/composition/use-pos-money';
  import {
    formatDateLong,
    formatTime,
    getReservationTimeZone,
  } from '@/domain/reservations/reservation-time';
  import type { IReservation } from '@/interfaces/IReservation';
  import type { IReservationCancelResult } from '@/interfaces/IReservationUi';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const props = defineProps<{
    reservation: Pick<
      IReservation,
      'guestName' | 'startsAt' | 'partySize' | 'depositStatus' | 'depositAmount'
    >;
    refundHours: number;
  }>();
  const modals = useModal();
  const money = usePosMoney();
  const restaurant = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const reason = ref('');
  const preset = ref('');
  const hasDeposit = computed(
    () =>
      props.reservation.depositStatus === 'paid' &&
      (props.reservation.depositAmount ?? 0) > 0
  );
  const refundable =
    Date.parse(props.reservation.startsAt) - Date.now() >=
    props.refundHours * 3600000;
  const refund = ref(refundable && hasDeposit.value);
  const reasons = ['guest', 'capacity', 'duplicate', 'other'];
  const zone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  const summary = computed(() =>
    String(
      proxy.$t('reservations.cancel.summary', {
        name:
          props.reservation.guestName ??
          proxy.$t('reservations.common.anonymous'),
        date: formatDateLong(
          props.reservation.startsAt,
          zone.value,
          proxy.$i18n.locale
        ),
        time: formatTime(
          props.reservation.startsAt,
          zone.value,
          proxy.$i18n.locale
        ),
        people: proxy.$tc(
          'reservations.common.people',
          props.reservation.partySize,
          { count: props.reservation.partySize }
        ),
      })
    )
  );
  const refundNote = computed(() =>
    String(
      proxy.$t(
        refund.value
          ? 'reservations.cancel.refundNote'
          : 'reservations.cancel.noRefundNote',
        {
          amount: money.formatComputedMoney(
            props.reservation.depositAmount ?? 0
          ),
        }
      )
    )
  );
  function selectReason(value: string) {
    preset.value = value;
    reason.value =
      value === 'other'
        ? ''
        : String(proxy.$t(`reservations.cancel.reason.${value}`));
  }
  function submit() {
    const result: IReservationCancelResult = {
      reason: reason.value.trim() || undefined,
      refundDeposit: hasDeposit.value && refund.value,
    };
    void modals.dialogSubmit('v2-reservation-cancel', result);
  }
</script>

<template>
  <div class="reservation-cancel-modal">
    <header>
      <h2>{{ $t('reservations.cancel.title') }}</h2>
      <p>{{ summary }}</p>
    </header>
    <div class="cancel-body">
      <label>{{ $t('reservations.cancel.reasonLabel') }}</label>
      <div class="reason-chips">
        <PosUiChip
          v-for="item in reasons"
          :key="item"
          :active="preset === item"
          @click="selectReason(item)"
          >{{ $t(`reservations.cancel.reason.${item}`) }}</PosUiChip
        >
      </div>
      <PosUiTextarea v-model="reason" :maxlength="255" :rows="3" />
      <div v-if="hasDeposit" class="refund-box">
        <label class="refund-check"
          ><input v-model="refund" type="checkbox" /><span>{{
            $t('reservations.cancel.refundDeposit')
          }}</span></label
        >
        <p>{{ refundNote }}</p>
      </div>
    </div>
    <footer>
      <PosUiButton
        color="neutral"
        variant="outline"
        @click="modals.hide('v2-reservation-cancel')"
        >{{ $t('reservations.cancel.keep') }}</PosUiButton
      ><PosUiButton color="error" variant="soft" @click="submit">{{
        $t('reservations.cancel.cta')
      }}</PosUiButton>
    </footer>
  </div>
</template>

<style scoped lang="scss">
  .reservation-cancel-modal {
    @apply tw-flex tw-max-h-[85vh] tw-flex-col tw-bg-white;
    header {
      @apply tw-border-b tw-border-neutral-200 tw-p-5;
      h2 {
        @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
      p {
        @apply tw-mt-1 tw-text-sm tw-text-neutral-500;
      }
    }
    .cancel-body {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-gap-3 tw-overflow-y-auto tw-p-5;
      > label {
        @apply tw-text-sm tw-font-medium tw-text-neutral-700;
      }
    }
    .reason-chips {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    .refund-box {
      @apply tw-rounded-xl tw-bg-neutral-100 tw-px-3 tw-pb-3;
      p {
        @apply tw-text-xs tw-text-neutral-500;
      }
    }
    .refund-check {
      @apply tw-flex tw-min-h-[44px] tw-items-center tw-gap-2 tw-text-sm tw-font-medium;
      input {
        @apply tw-h-4 tw-w-4;
        accent-color: #059669;
      }
    }
    footer {
      @apply tw-flex tw-flex-shrink-0 tw-flex-wrap tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-p-4;
    }
  }
</style>
